import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Settings.css';

// Fonction pour obtenir l'URL de l'API dynamiquement
const getApiUrlDynamic = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return '/api';
  }
  return 'http://localhost:8000/api';
};

const Settings = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('password');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [mailbox, setMailbox] = useState(null);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [showMailPassword, setShowMailPassword] = useState(false);
  const [copyMsg, setCopyMsg] = useState('');

  React.useEffect(() => {
    if (!token) return;
    setMailboxLoading(true);
    fetch(`${getApiUrlDynamic()}/mailboxes/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) setMailbox(d.mailbox || null);
      })
      .catch(() => {})
      .finally(() => setMailboxLoading(false));
  }, [token]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyMsg(`${label} copié !`);
      setTimeout(() => setCopyMsg(''), 2000);
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'Les nouveaux mots de passe ne correspondent pas.' });
      setLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Le nouveau mot de passe doit contenir au moins 6 caractères.' });
      setLoading(false);
      return;
    }

    try {
      const apiUrl = getApiUrlDynamic();
      const response = await fetch(`${apiUrl}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message || 'Mot de passe modifié avec succès !' });
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setMessage({ type: 'error', text: data.message || 'Erreur lors de la modification du mot de passe.' });
      }
    } catch (error) {
      console.error('Erreur lors de la modification du mot de passe:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la modification du mot de passe.' });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'password', label: 'Mot de passe', icon: '🔒' },
    { id: 'profile', label: 'Profil', icon: '👤' },
    { id: 'mailbox', label: 'Boîte mail', icon: '📧' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'security', label: 'Sécurité', icon: '🛡️' }
  ];

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1 className="settings-title">⚙️ Paramètres</h1>
        <p>Gérez vos préférences et paramètres de compte</p>
      </div>

      <div className="settings-content">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="nav-icon">{tab.icon}</span>
                <span className="nav-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="settings-main">
          {activeTab === 'password' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Changer le mot de passe</h2>
                <p>Sécurisez votre compte en modifiant régulièrement votre mot de passe</p>
              </div>

              <form className="password-form" onSubmit={handlePasswordSubmit}>
                <div className="form-group">
                  <label htmlFor="currentPassword">Mot de passe actuel</label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Entrez votre mot de passe actuel"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">Nouveau mot de passe</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Entrez votre nouveau mot de passe"
                    minLength="6"
                  />
                  <small className="form-help">
                    Le mot de passe doit contenir au moins 6 caractères
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Confirmez votre nouveau mot de passe"
                  />
                </div>

                {message.text && (
                  <div className={`message ${message.type}`}>
                    {message.text}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Modification...' : 'Modifier le mot de passe'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Informations du profil</h2>
                <p>Gérez vos informations personnelles</p>
              </div>
              <div className="profile-info">
                <div className="info-item">
                  <label>Nom d'utilisateur</label>
                  <span>{user?.username || 'Non défini'}</span>
                </div>
                <div className="info-item">
                  <label>Rôle</label>
                  <span>
                    {user?.roles && user.roles.includes('ROLE_ADMIN') ? 'Administrateur' : 
                     user?.roles && user.roles.length > 0 ? 'Utilisateur' : 'Non défini'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Dernière connexion</label>
                  <span>Il y a quelques minutes</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mailbox' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Boîte mail</h2>
                <p>Vos identifiants de messagerie @technonet.fr</p>
              </div>
              {mailboxLoading ? (
                <p style={{ color: '#6b7280' }}>Chargement...</p>
              ) : mailbox ? (
                <div className="profile-info">
                  {copyMsg && (
                    <div className="message success" style={{ marginBottom: '1rem' }}>{copyMsg}</div>
                  )}
                  <div className="info-item">
                    <label>Adresse email</label>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'monospace' }}>
                      {mailbox.email}
                      <button onClick={() => copyToClipboard(mailbox.email, 'Email')} title="Copier" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1rem' }}>📋</button>
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Mot de passe</label>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'monospace' }}>
                      {showMailPassword ? mailbox.password_clear : '••••••••••••'}
                      <button onClick={() => setShowMailPassword(p => !p)} title="Afficher" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1rem' }}>
                        {showMailPassword ? '🙈' : '👁'}
                      </button>
                      <button onClick={() => copyToClipboard(mailbox.password_clear, 'Mot de passe')} title="Copier" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1rem' }}>📋</button>
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Serveur IMAP</label>
                    <span style={{ fontFamily: 'monospace' }}>mail.technonet.fr — port 993 (SSL)</span>
                  </div>
                  <div className="info-item">
                    <label>Serveur SMTP</label>
                    <span style={{ fontFamily: 'monospace' }}>mail.technonet.fr — port 587 (STARTTLS)</span>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '8px', color: '#6b7280' }}>
                  <p style={{ margin: 0 }}>Aucune boîte mail associée à votre compte.<br />Contactez un administrateur pour en créer une.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Préférences de notification</h2>
                <p>Configurez comment vous souhaitez recevoir les notifications</p>
              </div>
              <div className="notification-settings">
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Notifications par email</h3>
                    <p>Recevez des notifications importantes par email</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Notifications push</h3>
                    <p>Recevez des notifications instantanées</p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Paramètres de sécurité</h2>
                <p>Gérez la sécurité de votre compte</p>
              </div>
              <div className="security-settings">
                <div className="security-item">
                  <div className="security-info">
                    <h3>Authentification à deux facteurs</h3>
                    <p>Ajoutez une couche de sécurité supplémentaire</p>
                  </div>
                  <button className="btn btn-secondary">Activer</button>
                </div>
                <div className="security-item">
                  <div className="security-info">
                    <h3>Sessions actives</h3>
                    <p>Gérez vos sessions de connexion actives</p>
                  </div>
                  <button className="btn btn-secondary">Voir les sessions</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
